import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Upload } from "lucide-react";

export default function AdminMockups() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingCats, setEditingCats] = useState(null);

  const load = () => api.get("/admin/mockup-templates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este mockup definitivamente?")) return;
    try {
      const { data } = await api.delete(`/admin/mockup-templates/${id}`);
      console.log("Mockup delete response:", data);
      toast.success("Mockup eliminado");
      load();
    } catch (e) {
      console.error("Delete mockup failed:", e);
      toast.error(formatApiError(e));
    }
  };

  const toggle = async (m) => {
    try {
      const payload = {
        name: m.name,
        image_path: m.image_path,
        hole_x: m.hole_x,
        hole_y: m.hole_y,
        hole_w: m.hole_w,
        hole_h: m.hole_h,
        is_active: !m.is_active,
      };
      await api.put(`/admin/mockup-templates/${m.id}`, payload);
      toast.success(payload.is_active ? "Activo" : "Inactivo");
      load();
    } catch (e) {
      console.error("Toggle mockup failed:", e);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-mockups">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl font-light tracking-tight">Galería de mockups</h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Fotos de paredes con un hueco marcado donde se proyectará automáticamente cualquier obra. El visitante podrá previsualizar cómo queda la obra en distintos contextos. Marca el área del hueco arrastrando el rectángulo.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 overline text-xs hover:bg-neutral-800" data-testid="mockup-add">
          <Plus size={14} /> Añadir mockup
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {items.map((m) => (
          <div key={m.id} className="bg-white border border-neutral-200" data-testid={`mockup-${m.id}`}>
            <div className="relative bg-neutral-100 aspect-[4/3] overflow-hidden">
              <img src={fileUrl(m.image_path)} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute border-2 border-emerald-500 bg-emerald-500/10"
                style={{ left: `${m.hole_x * 100}%`, top: `${m.hole_y * 100}%`, width: `${m.hole_w * 100}%`, height: `${m.hole_h * 100}%` }}
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg">{m.name}</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    Hueco: x:{(m.hole_x * 100).toFixed(0)}% y:{(m.hole_y * 100).toFixed(0)}% w:{(m.hole_w * 100).toFixed(0)}% h:{(m.hole_h * 100).toFixed(0)}%
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(m.categories || []).length === 0
                      ? <span className="overline text-[10px] text-neutral-500">Todas las categorías</span>
                      : (m.categories || []).map((c) => (
                        <span key={c} className="overline text-[10px] px-2 py-0.5 bg-neutral-100 border border-neutral-200">{c}</span>
                      ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => toggle(m)} className={`overline text-[10px] px-2 py-1 border ${m.is_active ? "border-emerald-600 text-emerald-700" : "border-neutral-300 text-neutral-500"}`} data-testid={`toggle-${m.id}`}>
                    {m.is_active ? "Activo" : "Inactivo"}
                  </button>
                  <button onClick={() => setEditingCats(m)} className="overline text-[10px] px-2 py-1 border border-neutral-300 hover:bg-neutral-50" data-testid={`edit-cats-${m.id}`}>
                    Categorías
                  </button>
                  <button onClick={() => remove(m.id)} className="overline text-[10px] px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50" data-testid={`del-${m.id}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="lg:col-span-2 bg-white border border-neutral-200 py-16 text-center overline text-neutral-400">
            Sin mockups todavía
          </div>
        )}
      </div>

      {creating && <CreateMockupModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editingCats && <EditCategoriesModal mockup={editingCats} onClose={() => setEditingCats(null)} onSaved={() => { setEditingCats(null); load(); }} />}
    </div>
  );
}

function EditCategoriesModal({ mockup, onClose, onSaved }) {
  const [allCategories, setAllCategories] = useState([]);
  const [selected, setSelected] = useState(mockup.categories || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setAllCategories(r.data || [])).catch(() => {});
  }, []);

  const toggle = (c) => setSelected((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/mockup-templates/${mockup.id}`, {
        name: mockup.name,
        image_path: mockup.image_path,
        hole_x: mockup.hole_x,
        hole_y: mockup.hole_y,
        hole_w: mockup.hole_w,
        hole_h: mockup.hole_h,
        is_active: mockup.is_active,
        categories: selected,
      });
      toast.success("Categorías actualizadas");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-cats-modal">
      <div className="bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-neutral-200">
          <h2 className="font-serif text-2xl font-light">Categorías · {mockup.name}</h2>
          <p className="text-xs text-neutral-500 mt-2">Vacío = se aplica a TODAS las obras. Con categorías seleccionadas solo se mostrará para obras de esas categorías.</p>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2">
            {allCategories.map((c) => (
              <button key={c} onClick={() => toggle(c)} className={`overline text-[10px] px-3 py-1.5 border ${selected.includes(c) ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-50"}`} data-testid={`cat-toggle-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 border-t border-neutral-200 flex justify-end gap-2">
          <button onClick={onClose} className="overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50">Cancelar</button>
          <button onClick={save} disabled={saving} className="overline text-xs px-6 py-2 bg-black text-white hover:bg-neutral-800 disabled:opacity-50" data-testid="save-cats">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateMockupModal({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [hole, setHole] = useState({ x: 0.3, y: 0.25, w: 0.4, h: 0.45 });
  const [drag, setDrag] = useState(null);
  const imgRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setAllCategories(r.data || [])).catch(() => {});
  }, []);

  const toggleCat = (c) => {
    setSelectedCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.path);
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const onMouseDown = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = imgRef.current.getBoundingClientRect();
    setDrag({
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startHole: { ...hole },
      width: rect.width,
      height: rect.height,
    });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = (e.clientX - drag.startX) / drag.width;
      const dy = (e.clientY - drag.startY) / drag.height;
      if (drag.mode === "move") {
        let nx = Math.max(0, Math.min(1 - drag.startHole.w, drag.startHole.x + dx));
        let ny = Math.max(0, Math.min(1 - drag.startHole.h, drag.startHole.y + dy));
        setHole({ ...drag.startHole, x: nx, y: ny });
      } else if (drag.mode === "resize") {
        let nw = Math.max(0.05, Math.min(1 - drag.startHole.x, drag.startHole.w + dx));
        let nh = Math.max(0.05, Math.min(1 - drag.startHole.y, drag.startHole.h + dy));
        setHole({ ...drag.startHole, w: nw, h: nh });
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag]);

  const save = async () => {
    if (!name.trim()) { toast.error("Pon un nombre al mockup"); return; }
    setSaving(true);
    try {
      await api.post("/admin/mockup-templates", {
        name: name.trim(),
        image_path: imagePath,
        hole_x: hole.x,
        hole_y: hole.y,
        hole_w: hole.w,
        hole_h: hole.h,
        is_active: true,
        categories: selectedCats,
      });
      toast.success("Mockup creado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="mockup-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-neutral-200 flex justify-between items-center">
          <h2 className="font-serif text-2xl font-light">Nuevo mockup</h2>
          <button onClick={onClose} className="text-2xl text-neutral-400 hover:text-black">×</button>
        </div>

        <div className="p-6 space-y-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (ej. Sala estar nórdica)" className="w-full border border-neutral-300 px-4 py-2.5 text-sm" data-testid="mockup-name" />

          {step === 1 && (
            <label className="block border-2 border-dashed border-neutral-300 p-12 text-center cursor-pointer hover:bg-neutral-50">
              <input type="file" accept="image/*" onChange={onFile} className="hidden" disabled={uploading} data-testid="mockup-file-input" />
              <Upload className="mx-auto mb-3 text-neutral-400" size={32} strokeWidth={1.2} />
              <div className="overline text-xs text-neutral-600">{uploading ? "Subiendo..." : "Subir foto de pared / contexto"}</div>
              <div className="text-xs text-neutral-500 mt-1">Idealmente con un cuadro / hueco claramente visible</div>
            </label>
          )}

          {step === 2 && imagePath && (
            <div>
              <div className="text-xs text-neutral-600 mb-3">
                Arrastra el rectángulo verde sobre la zona donde irá la obra. Arrastra la esquina inferior derecha para redimensionar.
              </div>
              <div ref={imgRef} className="relative bg-neutral-100 select-none" style={{ userSelect: "none" }}>
                <img src={preview} alt="" className="w-full block" draggable={false} />
                <div
                  onMouseDown={(e) => onMouseDown(e, "move")}
                  className="absolute border-2 border-emerald-500 bg-emerald-500/20 cursor-move"
                  style={{ left: `${hole.x * 100}%`, top: `${hole.y * 100}%`, width: `${hole.w * 100}%`, height: `${hole.h * 100}%` }}
                >
                  <div
                    onMouseDown={(e) => onMouseDown(e, "resize")}
                    className="absolute -bottom-2 -right-2 w-4 h-4 bg-emerald-600 cursor-se-resize"
                  />
                </div>
              </div>

              <div className="mt-5">
                <div className="overline text-[10px] text-neutral-500 mb-2">Categorías compatibles (opcional)</div>
                <p className="text-xs text-neutral-500 mb-2">Si seleccionas categorías, el mockup se aplicará solo a obras de esas categorías. Si dejas vacío, se aplica a TODAS las obras.</p>
                <div className="flex flex-wrap gap-2">
                  {allCategories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCat(c)}
                      className={`overline text-[10px] px-3 py-1.5 border ${selectedCats.includes(c) ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-50"}`}
                      data-testid={`mockup-cat-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-neutral-200">
            <button onClick={onClose} className="overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50">Cancelar</button>
            {step === 2 && (
              <button onClick={save} disabled={saving} className="overline text-xs px-6 py-2 bg-black text-white hover:bg-neutral-800 disabled:opacity-50" data-testid="mockup-save">
                {saving ? "Guardando..." : "Guardar mockup"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
